import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Flex, Spinner, Center } from "@chakra-ui/react"; // Using Spinner and Center for loading
import { useEffect, useState } from "react"; // 1. Import useState
import { Toaster } from "@/components/ui/toaster";
import { AuthHeader } from './AuthHeader';
import { useAuth } from "@/hooks/useAuth";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { configService } from '@/services/configService';
import type { OAuthConfig } from "@/types/auth"; // Assuming you have this type
import { routeTo } from "@/router/routeTo";


type AuthLayoutProps = {
  children?: React.ReactNode;
};


// No change here, but the usage will be fixed
const notRedirectIfAuthenticatedPaths = [routeTo.signOut()]; // TODO: Add routeTo.resetPassword() and routeTo.forgotPassword() if needed

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  // 2. Separate state for the OAuth config and its loading status
  const [config, setConfig] = useState<OAuthConfig | null>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  // The useAuth hook handles the user's authentication status
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // --- Effect 1: Fetch OAuth configuration ONCE on mount ---
  useEffect(() => {
    // Use our caching function to get the config.
    configService.getAuthLoginConfig()
      .then(setConfig)
      .catch((err: any) => {
        console.error("Failed to load OAuth config:", err);
        // You could set an error state here to show a message
      })
      .finally(() => {
        setIsConfigLoading(false); // Use our own loading state
      });
  }, []); // Empty dependency array means this runs only once

  // --- Effect 2: Handle redirection AFTER authentication status is known ---
  useEffect(() => {
    if (isLoading) return;

    const currentPath = location.pathname;
    const shouldRedirect =
      isAuthenticated && !notRedirectIfAuthenticatedPaths.includes(currentPath);

    if (shouldRedirect) {
      setTimeout(() => {
        if (currentPath.startsWith(routeTo.signOut())) {
          navigate(routeTo.help(), { replace: true });
        } else {
          navigate(routeTo.dashboard(), { replace: true });
        }
      }, 0);
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);
  // --- Render Logic ---

  // 4. Render a loading state until the config is fetched
  if (isConfigLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // Handle case where config failed to load
  if (!config) {
    return (
      <Center h="100vh">
        <div>Error: Could not load authentication provider. Please try again later.</div> { /* FIXME: Use Translation key*/}
      </Center>
    );
  }

  // The main layout is only rendered once config is available and loaded.
  // We don't need to check `isAuthLoading` here again, because if the user
  // is authenticated, the effect above will navigate them away.
  return (
    <Flex direction="column" minH="100vh" w="full">
      <Box w="full" h="80px">
        <AuthHeader />
      </Box>
      <Box as="main" flex="1" w="full" px={4} py={8} overflowY="auto" bg='bg.canvas'>
        <Toaster />
        {/* The provider now safely receives a valid client_id */}
        <GoogleOAuthProvider clientId={config.providers.google.client_id}>
          {children ?? <Outlet />}
        </GoogleOAuthProvider>
      </Box>
    </Flex>
  );
};